import { Box, Flex } from '@chakra-ui/react';
import { padStart, range } from 'lodash';
import Select, { Options, SingleValue, StylesConfig } from 'react-select';

import { ClockIcon } from '@sphere/icons';
import { set } from 'date-fns';
import { rem } from 'polished';
import { useCallback } from 'react';

const hours = range(24);
const minutes = range(0, 60, 5);

enum Period {
  AM = 'AM',
  PM = 'PM',
}

type Option = SingleValue<{
  label: string;
  value: string;
}>;

type Props = {
  date: Date;
  /** String representation of selected date value, e.g. `10:30` */
  value?: string;
  onChange(value: string): void;
  isValidDate?: (date: Date) => boolean;
};

const options: Options<Option> = hours
  .map(hour =>
    minutes.map(minute => ({
      label: `${padStart(`${hour > 12 ? hour - 12 : hour}`, 2, '0')}:${padStart(
        minute.toString(),
        2,
        '0',
      )} ${hour < 12 ? Period.AM : Period.PM}`,
      value: `${padStart(hour.toString(), 2, '0')}:${padStart(minute.toString(), 2, '0')}`,
    })),
  )
  .flat();

const customStyles: StylesConfig = {
  indicatorSeparator: () => ({
    display: 'none',
  }),
  // @ts-ignore
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected ? 'black' : state.isFocused ? '#F5F5F7' : 'unset',
  }),
};

function TimePicker({ onChange, date, value, isValidDate }: Props) {
  const defaultValue = options.find(option => option?.value === value);

  const isOptionDisabled = useCallback(
    (option: Option | null) => {
      if (!option || typeof isValidDate !== 'function') return false;

      const [hours, minutes] = option.value.split(':').map(n => +n);
      const validateDate = set(date, { hours, minutes });
      return !isValidDate(validateDate);
    },
    [date, isValidDate],
  );

  return (
    <Box onClick={e => e.stopPropagation()}>
      <Select<Option>
        placeholder="--:--"
        components={{
          DropdownIndicator: () => (
            <Flex align="center" px="space.8">
              <ClockIcon color="gray.5" boxSize={rem(20)} />
            </Flex>
          ),
        }}
        // @ts-ignore
        styles={customStyles}
        defaultValue={defaultValue}
        options={options}
        isOptionDisabled={isOptionDisabled}
        menuPlacement="top"
        onChange={(option: Option | null) => {
          if (option && option?.value) {
            onChange(option.value);
          }
        }}
      ></Select>
    </Box>
  );
}

export default TimePicker;
